import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { ReferralBoard } from "@/components/ReferralBoard";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, Check, Download, Instagram, Twitter, MessageCircle, Share2, Pencil, Eye, Link2, AtSign } from "lucide-react";

export function ShareProfileModal({ open, onClose, user }) {
  const [info, setInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setEditing(false);
    api.get("/creator/share").then(({ data }) => { setInfo(data); setHandle(data.handle); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load your share link"));
  }, [open]);

  if (!open) return null;
  const origin = window.location.origin;
  const url = info ? `${origin}/${info.handle}@velluto` : "";
  const address = info ? `${info.handle}@velluto` : "";
  const pretty = url.replace(/^https?:\/\//, "");
  const text = `Find me on Velluto Society ✦ ${user?.display_name || ""}`.trim();

  const copy = async (msg = "Link copied") => {
    try { await navigator.clipboard.writeText(url); } catch { /* fallback */ }
    setCopied(true); toast.success(msg); setTimeout(() => setCopied(false), 1800);
  };
  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
  const shareIG = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Velluto Society", text, url }); return; } catch { /* cancelled */ } }
    await copy("Link copied — paste it into your Instagram bio or story");
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };
  const nativeShare = async () => { try { await navigator.share({ title: "Velluto Society", text, url }); } catch { /* cancelled */ } };

  const saveHandle = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/creator/handle", { handle: handle.trim().toLowerCase() });
      setInfo((i) => ({ ...i, handle: data.handle })); setEditing(false); toast.success("Your link is updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update handle"); } finally { setSaving(false); }
  };

  const downloadQR = () => {
    const src = qrRef.current?.querySelector("canvas");
    if (!src) return;
    const pad = 64, W = src.width + pad * 2, H = src.height + pad * 2 + 90;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, W, H);
    ctx.drawImage(src, pad, pad);
    ctx.fillStyle = "#D4AF37"; ctx.textAlign = "center";
    ctx.font = "500 30px 'Cormorant Garamond', Georgia, serif"; ctx.fillText("Velluto Society", W / 2, src.height + pad + 44);
    ctx.fillStyle = "#F3F1EC"; ctx.font = "18px Manrope, sans-serif"; ctx.fillText(pretty, W / 2, src.height + pad + 74);
    const a = document.createElement("a"); a.download = `velluto-${info.handle}-qr.png`; a.href = c.toDataURL("image/png"); a.click();
    toast.success("QR code downloaded");
  };

  const Btn = ({ onClick, icon: Icon, label, testId }) => (
    <button data-testid={testId} onClick={onClick}
      className="flex flex-col items-center gap-2 py-4 rounded-2xl border border-white/10 bg-black/20 hover:border-gold/50 hover:bg-gold/5 transition-colors">
      <Icon size={20} className="text-gold" strokeWidth={1.6} /><span className="text-[10px] uppercase tracking-[0.15em] text-text-muted">{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-0 sm:p-4" onClick={onClose}>
      <div data-testid="share-modal" onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 bg-surface p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2"><Share2 size={13} /> Share your profile</p>
            <h2 className="font-display text-3xl font-light mt-2">Your door, your link</h2>
          </div>
          <button data-testid="share-close" onClick={onClose} className="p-2 rounded-full border border-white/10 text-text-muted hover:text-text-main"><X size={16} /></button>
        </div>

        {!info ? <p className="text-text-muted text-sm">Preparing your link…</p> : (
          <div className="grid sm:grid-cols-[1fr_200px] gap-6">
            <div className="space-y-5 min-w-0">
              <div data-testid="share-address-card" className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                <AtSign size={15} className="text-gold shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Your Velluto address</p>
                  <p data-testid="share-address" className="font-mono text-sm truncate">{address}</p>
                </div>
                <button data-testid="share-address-copy" onClick={async () => { try { await navigator.clipboard.writeText(address); } catch {} toast.success("Address copied"); }}
                  className="px-3 py-1.5 rounded-full border border-gold/40 text-gold text-[10px] uppercase tracking-[0.15em] hover:bg-gold/10">Copy</button>
              </div>
              <div className="rounded-2xl border border-gold/30 bg-black/30 p-4">
                {editing ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-muted shrink-0">{origin.replace(/^https?:\/\//, "")}/</span>
                    <input data-testid="share-handle-input" value={handle} onChange={(e) => setHandle(e.target.value)} maxLength={24} autoFocus
                      className="flex-1 min-w-0 bg-transparent border-b border-gold/50 outline-none text-sm py-1" />
                    <span className="text-xs text-text-muted shrink-0">@velluto</span>
                    <button data-testid="share-handle-save" disabled={saving} onClick={saveHandle} className="text-[10px] uppercase tracking-[0.15em] text-gold disabled:opacity-40">{saving ? "…" : "Save"}</button>
                    <button onClick={() => { setEditing(false); setHandle(info.handle); }} className="text-[10px] uppercase tracking-[0.15em] text-text-muted">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Link2 size={15} className="text-gold shrink-0" />
                    <p data-testid="share-url" className="flex-1 min-w-0 truncate text-sm">{pretty}</p>
                    <button data-testid="share-edit-handle" onClick={() => setEditing(true)} title="Customise handle" className="p-1.5 text-text-muted hover:text-gold"><Pencil size={13} /></button>
                    <button data-testid="share-copy" onClick={() => copy()}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.15em] hover:bg-gold/90 transition-colors">
                      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Btn onClick={shareIG} icon={Instagram} label="Instagram" testId="share-instagram" />
                <Btn onClick={shareX} icon={Twitter} label="X / Twitter" testId="share-twitter" />
                <Btn onClick={shareWA} icon={MessageCircle} label="WhatsApp" testId="share-whatsapp" />
              </div>
              {typeof navigator !== "undefined" && navigator.share && (
                <button data-testid="share-native" onClick={nativeShare} className="w-full py-3 rounded-full border border-white/15 text-xs uppercase tracking-[0.15em] text-text-muted hover:text-text-main transition-colors">More sharing options…</button>
              )}

              <div data-testid="share-stats" className="grid grid-cols-3 gap-2 text-center">
                {[["Link visits", info.visits], ["Sign-ups", info.signups], ["Earned", `$${((info.referral_earned || 0) / 100).toFixed(0)}`]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl border border-white/10 bg-black/20 py-3"><p className="font-display text-2xl leading-none">{v}</p><p className="text-[10px] uppercase tracking-[0.15em] text-text-muted mt-1">{l}</p></div>
                ))}
              </div>
              <ReferralBoard compact />
              <p data-testid="share-referral-copy" className="flex items-start gap-2 text-xs text-text-muted"><Eye size={13} className="shrink-0 mt-0.5" /> Earn ${((info.reward_cents || 1000) / 100).toFixed(0)} wallet credit every time someone joins through your link and becomes a verified member.</p>
            </div>

            <div className="flex flex-col items-center gap-3">
              <div ref={qrRef} data-testid="share-qr" className="p-3 rounded-2xl bg-ink border border-gold/30">
                <QRCodeCanvas value={url} size={160} bgColor="#0A0A0A" fgColor="#D4AF37" level="M" includeMargin={false} />
              </div>
              <button data-testid="share-qr-download" onClick={downloadQR}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-gold/40 text-gold text-[10px] uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors">
                <Download size={12} /> Download QR
              </button>
              <p className="text-[10px] text-text-muted text-center">For stories, flyers & event cards</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
